import { useApi, type JudoRestResponse } from "@judo/actions";
import { type CustomDialogProps, useDialog } from "@judo/feedback";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { extractFileNameFromToken } from "./file-utils";

/**
 * Return type of the {@link useFileHandling} hook.
 */
export interface FileHandling {
	/**
	 * Download a binary attribute's file.
	 *
	 * Reads the download token from `data[attributeName]` and triggers
	 * either a browser download (`attachment`) or an inline preview dialog (`inline`).
	 */
	downloadFile: (
		data: Record<string, unknown>,
		attributeName: string,
		disposition: "inline" | "attachment"
	) => Promise<void>;

	/**
	 * Upload a file to a binary attribute via the two-step upload flow.
	 * Returns the server response data (upload metadata).
	 */
	uploadFile: (attributePath: string, file: File) => Promise<unknown>;

	/**
	 * Extract the original filename from a JUDO binary download token (JWT).
	 * Returns `fallbackText` when the token is missing or cannot be decoded.
	 */
	extractFileNameFromToken: (token?: string | null, fallbackText?: string) => string;

	/**
	 * Trigger a browser file download from a REST API response that contains a Blob.
	 * Reads filename from the `Content-Disposition` header, falling back to `"download"`.
	 */
	exportFile: (response: JudoRestResponse<Blob>) => void;
}

/**
 * Extract filename from a `Content-Disposition` header value.
 * Returns `undefined` when the header is missing or does not contain a filename.
 */
function parseFileNameFromContentDisposition(header: string | null): string | undefined {
	if (!header) return undefined;
	const match = header.match(/filename="(.*)"/);
	return match?.[1];
}

/**
 * Trigger a browser download by creating a temporary anchor element.
 */
function triggerBrowserDownload(url: string, fileName: string): void {
	const link = document.createElement("a");
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

// ────────────────────────────────────────────
// Inline Preview Dialog
// ────────────────────────────────────────────

/** @internal Props for the inline preview dialog rendered by `downloadFile`. */
export interface InlinePreviewDialogProps extends CustomDialogProps<void> {
	url: string;
	fileName: string;
}

/**
 * Dialog component that previews a binary file in an iframe.
 * Opened by `useFileHandling().downloadFile(…, "inline")`.
 *
 * @internal
 */
export function InlinePreviewDialog({ onClose, url, fileName }: InlinePreviewDialogProps) {
	const { t } = useTranslation();
	return (
		<>
			<DialogTitle>
				{fileName}
				<IconButton
					data-testid="close-inline-binary-dialog-top-button"
					aria-label="close"
					onClick={() => onClose()}
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent dividers>
				<Box>
					<iframe
						src={url}
						title={fileName}
						sandbox="allow-same-origin"
						width="100%"
						height={typeof document !== "undefined" ? document.body.scrollHeight * 0.6 : 600}
						style={{ border: 0 }}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button data-testid="close-inline-binary-dialog-button" variant="text" onClick={() => onClose()}>
					{t("system.action.close.label", { defaultValue: "Close" })}
				</Button>
			</DialogActions>
		</>
	);
}

// ────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────

/**
 * Hook providing file handling operations: download, upload,
 * filename extraction from JWT tokens, and export-to-file.
 *
 * Adapted from the legacy `fileHandling` hook. Uses the JUDO REST
 * API client for upload/download and the feedback dialog service
 * for inline binary previews.
 *
 * @example
 * ```tsx
 * const { downloadFile, uploadFile, extractFileNameFromToken, exportFile } = useFileHandling();
 *
 * // Download as attachment
 * await downloadFile(transfer, "document", "attachment");
 *
 * // Preview inline
 * await downloadFile(transfer, "preview", "inline");
 *
 * // Upload a file
 * const result = await uploadFile("/admin/Issue/avatar", selectedFile);
 *
 * // Get display name from stored token
 * const name = extractFileNameFromToken(transfer.document, "Unnamed file");
 * ```
 */
export function useFileHandling(): FileHandling {
	const api = useApi();
	const dialog = useDialog();

	const extractFn = useCallback(
		(token?: string | null, fallbackText?: string): string => extractFileNameFromToken(token, fallbackText),
		[]
	);

	const exportFile = useCallback((response: JudoRestResponse<Blob>): void => {
		const fileName = parseFileNameFromContentDisposition(response.headers.get("content-disposition")) ?? "download";
		const contentType = response.headers.get("content-type");
		const blob = contentType ? new Blob([response.data], { type: contentType }) : response.data;
		const url = URL.createObjectURL(blob);
		triggerBrowserDownload(url, fileName);
		URL.revokeObjectURL(url);
	}, []);

	const downloadFile = useCallback(
		async (
			data: Record<string, unknown>,
			attributeName: string,
			disposition: "inline" | "attachment"
		): Promise<void> => {
			const token = data[attributeName];
			if (typeof token !== "string") {
				throw new Error(`No download token found for attribute "${attributeName}"`);
			}

			const response = await api.downloadFile(token, disposition);

			const contentDisposition = response.headers.get("content-disposition");
			const contentType = response.headers.get("content-type");
			const fileName =
				parseFileNameFromContentDisposition(contentDisposition) ?? extractFileNameFromToken(token, "download");

			const blob = contentType ? new Blob([response.data], { type: contentType }) : response.data;
			const url = URL.createObjectURL(blob);

			if (disposition === "inline") {
				try {
					await dialog.open<void, InlinePreviewDialogProps>({
						component: InlinePreviewDialog,
						props: { url, fileName },
						size: "lg",
						fullWidth: true,
						disableBackdropClick: true,
					});
				} finally {
					URL.revokeObjectURL(url);
				}
			} else {
				triggerBrowserDownload(url, fileName);
				URL.revokeObjectURL(url);
			}
		},
		[api, dialog]
	);

	const uploadFile = useCallback(
		async (attributePath: string, file: File): Promise<unknown> => {
			const response = await api.uploadFile(attributePath, file);
			return response.data;
		},
		[api]
	);

	return {
		downloadFile,
		uploadFile,
		extractFileNameFromToken: extractFn,
		exportFile,
	};
}
